import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, startOfWeek, addDays, isSameDay, parseISO } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Save, Send, Loader2, MessageSquare, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TimesheetPage() {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [assignments, setAssignments] = useState([]);
  const [entries, setEntries] = useState([]);
  const [plannedHours, setPlannedHours] = useState({}); // { project_id: { date: hours } }
  const [clientSettings, setClientSettings] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [vacationDates, setVacationDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { projectId, date } for no-task projects
  const [cellValue, setCellValue] = useState('');
  // Task-aware state
  const [projectTasks, setProjectTasks] = useState({}); // { project_id: Task[] }
  const [taskHoursInput, setTaskHoursInput] = useState({}); // { project_id: { date: { task_id: value_string } } }
  const [editingTaskCell, setEditingTaskCell] = useState(null); // { projectId, taskId, date }
  const [notesDialog, setNotesDialog] = useState({ open: false, projectId: null, date: null, notes: '' });
  const [lockedMonths, setLockedMonths] = useState([]);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEnd = addDays(weekStart, 6);

  const currentWeekMonth = format(weekStart, 'yyyy-MM');
  const isCurrentWeekLocked = lockedMonths.includes(currentWeekMonth);

  const fetchData = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const endDate = addDays(weekStart, 6);
      const startDateStr = format(weekStart, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');

      const [assignmentsRes, entriesRes, plannedRes, scheduleRes] = await Promise.all([
        axios.get(`${API_URL}/api/assignments/user/${user.id}`),
        axios.get(`${API_URL}/api/timesheet/entries`, {
          params: {
            start_date: startDateStr,
            end_date: endDateStr
          }
        }),
        axios.get(`${API_URL}/api/daily-plans/user/${user.id}`, {
          params: {
            start_date: startDateStr,
            end_date: endDateStr
          }
        }).catch((error) => {
          console.error('Failed to load planned hours:', error.response?.status, error.response?.data);
          return { data: {} };
        }),
        axios.get(`${API_URL}/api/employee-schedules/${user.id}`).catch(() => ({ data: { vacation_dates: [] } }))
      ]);
      setAssignments(assignmentsRes.data);
      setEntries(entriesRes.data);
      setPlannedHours(plannedRes.data || {});
      setVacationDates(scheduleRes.data?.vacation_dates || []);

      // Fetch locked months
      try {
        const locksRes = await axios.get(`${API_URL}/api/month-locks`);
        setLockedMonths(locksRes.data.map(l => l.year_month));
      } catch (e) {
        // Non-critical
      }

      // Fetch tasks for each assigned project
      const tasksMap = {};
      await Promise.all(
        assignmentsRes.data.map(async (a) => {
          try {
            const tasksRes = await axios.get(`${API_URL}/api/projects/${a.project_id}/tasks`);
            tasksMap[a.project_id] = tasksRes.data;
          } catch {
            tasksMap[a.project_id] = [];
          }
        })
      );
      setProjectTasks(tasksMap);

      // Populate taskHoursInput from loaded entries
      const inputMap = {};
      for (const entry of entriesRes.data) {
        if (entry.task_hours && entry.task_hours.length > 0) {
          if (!inputMap[entry.project_id]) inputMap[entry.project_id] = {};
          if (!inputMap[entry.project_id][entry.work_date]) inputMap[entry.project_id][entry.work_date] = {};
          for (const th of entry.task_hours) {
            inputMap[entry.project_id][entry.work_date][th.task_id] = th.hours.toString();
          }
        }
      }
      setTaskHoursInput(inputMap);

      // Fetch client settings and holidays for the first assignment's project
      if (assignmentsRes.data.length > 0) {
        const projectId = assignmentsRes.data[0].project_id;
        try {
          const projectRes = await axios.get(`${API_URL}/api/projects/${projectId}`);
          const project = projectRes.data;
          if (project.client_id) {
            const [clientRes, holidaysRes] = await Promise.all([
              axios.get(`${API_URL}/api/clients/${project.client_id}`),
              axios.get(`${API_URL}/api/clients/${project.client_id}/holidays`)
            ]);
            setClientSettings({
              allow_weekend_work: clientRes.data.allow_weekend_work === true,
              allow_holiday_work: clientRes.data.allow_holiday_work === true,
            });
            setHolidays(holidaysRes.data.map(h => h.date));
          } else {
            // No client assigned - allow entry on all days
            setClientSettings({ allow_weekend_work: true, allow_holiday_work: true });
            setHolidays([]);
          }
        } catch (e) {
          // Client not found or error - allow entry on all days
          setClientSettings({ allow_weekend_work: true, allow_holiday_work: true });
          setHolidays([]);
        }
      } else {
        // No assignments - allow entry on all days
        setClientSettings({ allow_weekend_work: true, allow_holiday_work: true });
        setHolidays([]);
      }
    } catch (error) {
      toast.error('Failed to load timesheet data');
    } finally {
      setLoading(false);
    }
  }, [user?.id, weekStart]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Helper to check if a date is a weekend
  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday or Saturday
  };

  // Helper to check if a date is a holiday
  const isHoliday = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidays.includes(dateStr);
  };

  // Helper to check if a date is a vacation day
  const isVacation = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return vacationDates.includes(dateStr);
  };

  // Get cell background color based on date type
  const getCellBackground = (date) => {
    if (isVacation(date)) {
      return 'bg-purple-100 dark:bg-purple-950/30'; // Purple for vacation
    }
    if (isHoliday(date)) {
      return 'bg-red-100 dark:bg-red-950/30'; // Red for holidays
    }
    if (isWeekend(date)) {
      return 'bg-orange-100 dark:bg-orange-950/30'; // Orange for weekends
    }
    return '';
  };

  // Check if time entry is allowed for a date
  const isEntryAllowed = (date) => {
    // Vacation days are never allowed for time entry
    if (isVacation(date)) {
      return false;
    }
    // If no client settings loaded yet, allow entry on weekends/holidays by default
    // The backend will enforce restrictions if any
    if (isHoliday(date) && clientSettings && !clientSettings.allow_holiday_work) {
      return false;
    }
    if (isWeekend(date) && clientSettings && !clientSettings.allow_weekend_work) {
      return false;
    }
    return true;
  };

  const getEntry = (projectId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return entries.find(e => e.project_id === projectId && e.work_date === dateStr);
  };

  const getHours = (projectId, date) => {
    const entry = getEntry(projectId, date);
    return entry?.hours || 0;
  };

  const getPlannedHours = (projectId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return plannedHours[projectId]?.[dateStr] || 0;
  };

  const getRowTotal = (projectId) => {
    return weekDays.reduce((sum, day) => sum + getHours(projectId, day), 0);
  };

  const getColumnTotal = (date) => {
    return assignments.reduce((sum, a) => sum + getHours(a.project_id, date), 0);
  };

  const getWeekTotal = () => {
    return assignments.reduce((sum, a) => sum + getRowTotal(a.project_id), 0);
  };

  // Task helpers
  const getTaskInputValue = (projectId, taskId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return taskHoursInput[projectId]?.[dateStr]?.[taskId] ?? '';
  };

  const setTaskInputValue = (projectId, taskId, date, value) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    setTaskHoursInput(prev => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        [dateStr]: {
          ...(prev[projectId]?.[dateStr] || {}),
          [taskId]: value,
        }
      }
    }));
  };

  const getProjectDayTaskTotal = (projectId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayMap = taskHoursInput[projectId]?.[dateStr] || {};
    return Object.values(dayMap).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  };

  const handleTaskCellBlur = async (projectId, taskId, date) => {
    setEditingTaskCell(null);
    if (!isEntryAllowed(date) || isCurrentWeekLocked) return;

    const dateStr = format(date, 'yyyy-MM-dd');
    const tasks = projectTasks[projectId] || [];
    const dayMap = taskHoursInput[projectId]?.[dateStr] || {};

    // Build task_hours payload (only non-zero)
    const taskHoursPayload = tasks
      .map(t => ({ task_id: t.id, hours: parseFloat(dayMap[t.id]) || 0 }))
      .filter(th => th.hours > 0);

    const total = taskHoursPayload.reduce((s, th) => s + th.hours, 0);
    if (total < 0 || total > 24) {
      toast.error('Total hours for the day must be between 0 and 24');
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/timesheet/entries`, {
        project_id: projectId,
        work_date: dateStr,
        hours: total,
        task_hours: taskHoursPayload,
        notes: ''
      });
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptPlanned = async (projectId, date) => {
    if (!isEntryAllowed(date)) {
      if (isHoliday(date)) {
        toast.error('Time entry not allowed on holidays');
      } else if (isWeekend(date)) {
        toast.error('Time entry not allowed on weekends');
      }
      return;
    }

    const planned = getPlannedHours(projectId, date);
    if (planned <= 0) {
      toast.error('No planned hours for this day');
      return;
    }

    if (isCurrentWeekLocked) {
      toast.error('This month is locked and cannot be modified');
      return;
    }

    // If project has tasks, distribute planned hours equally across tasks
    const tasks = projectTasks[projectId] || [];
    if (tasks.length > 0) {
      const perTask = Math.round((planned / tasks.length) * 100) / 100;
      const dateStr = format(date, 'yyyy-MM-dd');
      const newMap = {};
      tasks.forEach(t => { newMap[t.id] = perTask.toString(); });
      setTaskHoursInput(prev => ({
        ...prev,
        [projectId]: { ...(prev[projectId] || {}), [dateStr]: newMap }
      }));
      // Then save
      try {
        setSaving(true);
        const taskHoursPayload = tasks.map(t => ({ task_id: t.id, hours: perTask }));
        await axios.post(`${API_URL}/api/timesheet/entries`, {
          project_id: projectId,
          work_date: dateStr,
          hours: planned,
          task_hours: taskHoursPayload,
          notes: ''
        });
        await fetchData();
        toast.success(`Accepted ${planned}h planned hours`);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to save entry');
      } finally {
        setSaving(false);
      }
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/timesheet/entries`, {
        project_id: projectId,
        work_date: format(date, 'yyyy-MM-dd'),
        hours: planned,
        notes: ''
      });
      await fetchData();
      toast.success(`Accepted ${planned}h planned hours`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleCellClick = (projectId, date, currentHours) => {
    if (!isEntryAllowed(date)) {
      if (isHoliday(date)) {
        toast.error('Time entry not allowed on holidays for this client');
      } else if (isWeekend(date)) {
        toast.error('Time entry not allowed on weekends for this client');
      }
      return;
    }

    if (isCurrentWeekLocked) {
      toast.error('This month is locked and cannot be modified');
      return;
    }
    setEditingCell({ projectId, date: format(date, 'yyyy-MM-dd') });
    setCellValue(currentHours > 0 ? currentHours.toString() : '');
  };

  const handleCellBlur = async () => {
    if (!editingCell) return;

    const hours = parseFloat(cellValue) || 0;
    if (hours < 0 || hours > 24) {
      toast.error('Hours must be between 0 and 24');
      setEditingCell(null);
      setCellValue('');
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/timesheet/entries`, {
        project_id: editingCell.projectId,
        work_date: editingCell.date,
        hours: hours,
        notes: ''
      });
      await fetchData();
      toast.success('Entry saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    } finally {
      setSaving(false);
      setEditingCell(null);
      setCellValue('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleCellBlur();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setCellValue('');
    }
  };

  const handleSubmitWeek = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/timesheet/submit`, {
        period_start: format(weekStart, 'yyyy-MM-dd'),
        period_end: format(weekEnd, 'yyyy-MM-dd')
      });
      await fetchData();
      toast.success('Timesheet submitted for approval');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  const openNotesDialog = (projectId, date) => {
    const entry = getEntry(projectId, date);
    setNotesDialog({
      open: true,
      projectId,
      date: format(date, 'yyyy-MM-dd'),
      notes: entry?.notes || ''
    });
  };

  const saveNotes = async () => {
    try {
      const entry = entries.find(e => e.project_id === notesDialog.projectId && e.work_date === notesDialog.date);
      if (entry) {
        await axios.put(`${API_URL}/api/timesheet/entries/${entry.id}`, {
          notes: notesDialog.notes
        });
      } else {
        await axios.post(`${API_URL}/api/timesheet/entries`, {
          project_id: notesDialog.projectId,
          work_date: notesDialog.date,
          hours: 0,
          notes: notesDialog.notes
        });
      }
      await fetchData();
      toast.success('Notes saved');
    } catch (error) {
      toast.error('Failed to save notes');
    }
    setNotesDialog({ open: false, projectId: null, date: null, notes: '' });
  };

  const isToday = (date) => isSameDay(date, new Date());

  const hasEntriesThisWeek = entries.length > 0;
  const weekHasSubmittedOrApproved = entries.some(e => e.status === 'SUBMITTED' || e.status === 'APPROVED');
  // Allow resubmit as long as month is not locked and there are entries
  const submitButtonLabel = weekHasSubmittedOrApproved ? 'Resubmit Week' : 'Submit Week';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Week Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            data-testid="prev-week-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="min-w-[200px]" data-testid="week-picker">
                <CalendarIcon className="w-4 h-4 mr-2" />
                {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={weekStart}
                onSelect={(date) => date && setWeekStart(startOfWeek(date, { weekStartsOn: 1 }))}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <Button
            variant="outline"
            size="icon"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            data-testid="next-week-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
            data-testid="today-btn"
          >
            Today
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {saving && (
            <span className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </span>
          )}
          {isCurrentWeekLocked ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted px-3 py-2 rounded-md">
              <Lock className="w-4 h-4" />
              Month Locked
            </div>
          ) : (
            <Button
              onClick={handleSubmitWeek}
              disabled={submitting || getWeekTotal() === 0}
              variant={weekHasSubmittedOrApproved ? 'outline' : 'default'}
              data-testid="submit-week-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {submitButtonLabel}
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* No assignments message */}
      {assignments.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              You are not assigned to any projects yet. Please contact your manager.
            </p>
          </CardContent>
        </Card>
      ) : (
        /* Timesheet Grid */
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 font-semibold text-sm text-muted-foreground w-48">
                    Project
                  </th>
                  {weekDays.map((day) => (
                    <th
                      key={day.toISOString()}
                      className={cn(
                        "text-center p-2 font-medium text-sm w-20",
                        isToday(day) && "bg-primary/10",
                        isHoliday(day) && "bg-red-100 dark:bg-red-950/30",
                        isWeekend(day) && !isHoliday(day) && "bg-orange-100 dark:bg-orange-950/30"
                      )}
                    >
                      <div className={cn(
                        "text-muted-foreground",
                        isHoliday(day) && "text-red-600 dark:text-red-400",
                        isWeekend(day) && !isHoliday(day) && "text-orange-600 dark:text-orange-400"
                      )}>{format(day, 'EEE')}</div>
                      <div className={cn(
                        "text-lg",
                        isToday(day) && "text-primary font-bold",
                        isHoliday(day) && !isToday(day) && "text-red-600 dark:text-red-400",
                        isWeekend(day) && !isHoliday(day) && !isToday(day) && "text-orange-600 dark:text-orange-400"
                      )}>
                        {format(day, 'd')}
                      </div>
                      {isHoliday(day) && (
                        <div className="text-[10px] text-red-600 dark:text-red-400">Holiday</div>
                      )}
                    </th>
                  ))}
                  <th className="text-center p-4 font-semibold text-sm w-20">Total</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment) => {
                  const tasks = projectTasks[assignment.project_id] || [];
                  const hasTasks = tasks.length > 0;
                  const entry = getEntry(assignment.project_id, weekDays[0]); // for notes check

                  if (hasTasks) {
                    // ── Task-aware rows ──
                    return (
                      <>
                        {/* Project header row — read-only totals */}
                        <tr key={`hdr-${assignment.id}`} className="border-b border-border bg-muted/20">
                          <td className="p-4 pb-1">
                            <div className="font-semibold text-sm">{assignment.project_name}</div>
                            <div className="text-xs text-muted-foreground">{assignment.project_code}</div>
                          </td>
                          {weekDays.map((day) => {
                            const total = getHours(assignment.project_id, day);
                            const planned = getPlannedHours(assignment.project_id, day);
                            const entryBlocked = !isEntryAllowed(day);
                            const dayEntry = getEntry(assignment.project_id, day);
                            const isLocked = dayEntry?.status === 'APPROVED' || dayEntry?.status === 'SUBMITTED';
                            return (
                              <td key={day.toISOString()} className={cn(
                                "text-center p-1",
                                isToday(day) && "bg-primary/10",
                                !isToday(day) && getCellBackground(day)
                              )}>
                                <div className="flex flex-col items-center gap-0.5">
                                  {planned > 0 && !entryBlocked && (
                                    <div
                                      className="text-[10px] text-muted-foreground cursor-pointer hover:text-primary transition-colors"
                                      onClick={() => !isLocked && handleAcceptPlanned(assignment.project_id, day)}
                                      title="Click to distribute planned hours across tasks"
                                    >
                                      Plan: {planned}h
                                    </div>
                                  )}
                                  <div className={cn(
                                    "w-16 h-8 rounded-md text-sm font-semibold flex items-center justify-center",
                                    total > 0 ? "bg-primary/10 text-primary" : "text-muted-foreground"
                                  )}>
                                    {total > 0 ? `${total}h` : '—'}
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                          <td className="text-center p-4 font-semibold text-sm">
                            {getRowTotal(assignment.project_id)}h
                          </td>
                        </tr>
                        {/* Task sub-rows */}
                        {tasks.map((task) => (
                          <tr key={`task-${task.id}`} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                            <td className="pl-8 pr-4 py-2">
                              <div className="text-sm text-muted-foreground flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-muted-foreground/40 inline-block shrink-0" />
                                {task.name}
                                {task.code && <span className="text-xs opacity-60">({task.code})</span>}
                              </div>
                            </td>
                            {weekDays.map((day) => {
                              const entryBlocked = !isEntryAllowed(day);
                              const dayEntry = getEntry(assignment.project_id, day);
                              const isLocked = (dayEntry?.status === 'APPROVED' || dayEntry?.status === 'SUBMITTED') && isCurrentWeekLocked;
                              const isEditingThis = editingTaskCell?.projectId === assignment.project_id &&
                                                    editingTaskCell?.taskId === task.id &&
                                                    editingTaskCell?.date === format(day, 'yyyy-MM-dd');
                              const inputVal = getTaskInputValue(assignment.project_id, task.id, day);
                              const displayVal = parseFloat(inputVal) || 0;

                              return (
                                <td key={day.toISOString()} className={cn(
                                  "text-center p-1",
                                  isToday(day) && "bg-primary/10",
                                  !isToday(day) && getCellBackground(day)
                                )}>
                                  {isEditingThis ? (
                                    <Input
                                      type="number"
                                      step="0.25"
                                      min="0"
                                      max="24"
                                      value={inputVal}
                                      onChange={(e) => setTaskInputValue(assignment.project_id, task.id, day, e.target.value)}
                                      onBlur={() => handleTaskCellBlur(assignment.project_id, task.id, day)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleTaskCellBlur(assignment.project_id, task.id, day);
                                        if (e.key === 'Escape') setEditingTaskCell(null);
                                      }}
                                      autoFocus
                                      className="w-16 h-9 text-center mx-auto text-sm"
                                    />
                                  ) : (
                                    <button
                                      onClick={() => {
                                        if (entryBlocked) {
                                          if (isHoliday(day)) toast.error('Not allowed on holidays');
                                          else if (isWeekend(day)) toast.error('Not allowed on weekends');
                                          return;
                                        }
                                        if (isCurrentWeekLocked) { toast.error('Month is locked'); return; }
                                        setEditingTaskCell({ projectId: assignment.project_id, taskId: task.id, date: format(day, 'yyyy-MM-dd') });
                                      }}
                                      disabled={entryBlocked || isLocked}
                                      className={cn(
                                        "w-16 h-9 rounded text-sm transition-all mx-auto flex items-center justify-center",
                                        displayVal > 0
                                          ? "bg-primary/10 text-primary hover:bg-primary/20"
                                          : "bg-transparent hover:bg-muted/60 text-muted-foreground",
                                        (entryBlocked || isLocked) && "opacity-40 cursor-not-allowed"
                                      )}
                                    >
                                      {displayVal > 0 ? displayVal : '·'}
                                    </button>
                                  )}
                                </td>
                              );
                            })}
                            <td className="text-center p-2 text-sm text-muted-foreground">
                              {weekDays.reduce((s, d) => s + (parseFloat(getTaskInputValue(assignment.project_id, task.id, d)) || 0), 0) || ''}
                            </td>
                          </tr>
                        ))}
                      </>
                    );
                  }

                  // ── No-task row (unchanged behavior) ──
                  return (
                    <tr key={assignment.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                      <td className="p-4">
                        <div className="font-medium">{assignment.project_name}</div>
                        <div className="text-xs text-muted-foreground">{assignment.project_code}</div>
                      </td>
                      {weekDays.map((day) => {
                        const hours = getHours(assignment.project_id, day);
                        const dayEntry = getEntry(assignment.project_id, day);
                        const isEditing = editingCell?.projectId === assignment.project_id &&
                                         editingCell?.date === format(day, 'yyyy-MM-dd');
                        const isLocked = dayEntry?.status === 'APPROVED' || dayEntry?.status === 'SUBMITTED';
                        const planned = getPlannedHours(assignment.project_id, day);
                        const entryBlocked = !isEntryAllowed(day);

                        return (
                          <td key={day.toISOString()} className={cn(
                            "text-center p-1",
                            isToday(day) && "bg-primary/10",
                            !isToday(day) && getCellBackground(day)
                          )}>
                            <div className="relative group flex flex-col items-center gap-0.5">
                              {planned > 0 && !entryBlocked && (
                                <div
                                  className="text-[10px] text-muted-foreground cursor-pointer hover:text-primary transition-colors"
                                  onClick={() => !isLocked && !isEditing && handleAcceptPlanned(assignment.project_id, day)}
                                  title={isLocked ? "Entry is locked" : "Click to accept planned hours"}
                                >
                                  Plan: {planned}h
                                </div>
                              )}
                              {isEditing ? (
                                <Input
                                  type="number"
                                  step="0.25"
                                  min="0"
                                  max="24"
                                  value={cellValue}
                                  onChange={(e) => setCellValue(e.target.value)}
                                  onBlur={handleCellBlur}
                                  onKeyDown={handleKeyDown}
                                  autoFocus
                                  className="w-16 h-10 text-center mx-auto"
                                  data-testid={`entry-input-${assignment.project_id}-${format(day, 'yyyy-MM-dd')}`}
                                />
                              ) : (
                                <button
                                  onClick={() => handleCellClick(assignment.project_id, day, hours)}
                                  disabled={isLocked || entryBlocked}
                                  className={cn(
                                    "w-16 h-10 rounded-md text-sm font-medium transition-all mx-auto flex items-center justify-center",
                                    hours > 0
                                      ? "bg-primary/10 text-primary hover:bg-primary/20"
                                      : "bg-muted hover:bg-muted/80",
                                    (isLocked || entryBlocked) && "opacity-50 cursor-not-allowed",
                                    planned > 0 && hours === 0 && !entryBlocked && "border border-dashed border-primary/40"
                                  )}
                                  data-testid={`entry-cell-${assignment.project_id}-${format(day, 'yyyy-MM-dd')}`}
                                >
                                  {hours > 0 ? hours : '-'}
                                </button>
                              )}
                              {dayEntry?.notes && (
                                <div className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full" />
                              )}
                              {!isEditing && !isLocked && (
                                <button
                                  onClick={() => openNotesDialog(assignment.project_id, day)}
                                  className="absolute -bottom-1 -right-1 w-5 h-5 bg-background border border-border rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                  data-testid={`notes-btn-${assignment.project_id}-${format(day, 'yyyy-MM-dd')}`}
                                >
                                  <MessageSquare className="w-3 h-3 text-muted-foreground" />
                                </button>
                              )}
                            </div>
                          </td>
                        );
                      })}
                      <td className="text-center p-4 font-semibold">
                        {getRowTotal(assignment.project_id)}h
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-muted/50">
                  <td className="p-4 font-semibold">Daily Total</td>
                  {weekDays.map((day) => (
                    <td
                      key={day.toISOString()}
                      className={cn(
                        "text-center p-4 font-semibold",
                        isToday(day) && "bg-primary/10"
                      )}
                    >
                      {getColumnTotal(day)}h
                    </td>
                  ))}
                  <td className="text-center p-4 font-bold text-primary text-lg">
                    {getWeekTotal()}h
                  </td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Week Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{getWeekTotal()}h</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{assignments.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Status</CardTitle>
          </CardHeader>
          <CardContent>
            {weekHasSubmittedOrApproved ? (
              <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                Submitted
              </Badge>
            ) : (
              <Badge variant="secondary">Draft</Badge>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notes Dialog */}
      <Dialog open={notesDialog.open} onOpenChange={(open) => !open && setNotesDialog({ open: false, projectId: null, date: null, notes: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Notes</DialogTitle>
            <DialogDescription>
              Add notes for this time entry
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={notesDialog.notes}
            onChange={(e) => setNotesDialog({ ...notesDialog, notes: e.target.value })}
            placeholder="Enter your notes here..."
            rows={4}
            data-testid="notes-textarea"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesDialog({ open: false, projectId: null, date: null, notes: '' })}>
              Cancel
            </Button>
            <Button onClick={saveNotes} data-testid="save-notes-btn">
              <Save className="w-4 h-4 mr-2" />
              Save Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
